import React, { useState, useEffect, useMemo } from 'react';
import { Container, Card, Table, Button, Form, InputGroup, Pagination, Row, Col } from 'react-bootstrap';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { FaSearch, FaChevronLeft, FaChevronRight, FaPrint } from 'react-icons/fa';
import BidForm from '../components/bids/BidForm';
import { getBids, getBid } from '../services/bidService';
import { toast } from 'react-toastify';
import './Bids.css';

const Bids = () => {
  const { bidId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const estimateId = queryParams.get('estimateId');
  
  // State management
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // You can make this configurable
  
  useEffect(() => {
    // If there's a bidId or estimateId, we don't need to fetch all bids
    if (!bidId && !estimateId) {
      fetchBids();
    }
  }, [bidId, estimateId]);
  
  /**
   * Fetches all bids from the API and sorts them by most recent
   */
  const fetchBids = async () => {
    setLoading(true);
    try {
      const data = await getBids();
      // Sort bids by created_at in descending order (most recent first)
      const sortedBids = data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setBids(sortedBids);
    } catch (error) {
      console.error('Error fetching bids:', error);
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Formats datetime string to a readable format
   */
  const formatDate = (dateString) => {
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  /**
   * Formats date string (YYYY-MM-DD) to a readable format
   */
  const formatDateOnly = (dateString) => {
    if (!dateString) return 'N/A';
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    return new Date(dateString + 'T00:00:00').toLocaleDateString(undefined, options);
  };
  
  /**
   * Filters bids based on search term
   * Searches through customer name, bid ID, and site name
   */
  const filteredBids = useMemo(() => {
    if (!searchTerm.trim()) return bids;
    
    const searchLower = searchTerm.toLowerCase();
    return bids.filter(bid => {
      const bidId = `BID-${bid.id}`.toLowerCase();
      const customerName = (bid.customer_name || '').toLowerCase();
      const siteName = (bid.site_name || '').toLowerCase();
      
      return bidId.includes(searchLower) || 
             customerName.includes(searchLower) ||
             siteName.includes(searchLower);
    });
  }, [bids, searchTerm]);
  
  /**
   * Calculate pagination values
   */
  const paginationData = useMemo(() => {
    const totalItems = filteredBids.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentBids = filteredBids.slice(startIndex, endIndex);
    
    return {
      currentBids,
      totalPages,
      totalItems,
      startIndex,
      endIndex: Math.min(endIndex, totalItems)
    };
  }, [filteredBids, currentPage, itemsPerPage]);
  
  /**
   * Handle search input changes
   */
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };
  
  /**
   * Handle page changes
   */
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  /**
   * Generate pagination items for the Pagination component
   */

  /**
   * Handle printing a bid - opens a clean print window
   */
  const handlePrintBid = async (bid, e) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      // Fetch full bid data
      const fullBid = await getBid(bid.id);

      // Create print window
      const printWindow = window.open('', '_blank', 'width=800,height=600');

      // Generate door sections with line items in compact format
      const doorSections = fullBid.doors?.map(door => {
        const lineItemsHtml = door.line_items?.length > 0
          ? door.line_items.map(item => `
            <tr class="line-item-row">
              <td class="desc-cell">${item.description || ''}</td>
              <td class="num-cell">${item.quantity || 0}</td>
              <td class="num-cell">$${(item.price || 0).toFixed(2)}</td>
              <td class="num-cell">$${((item.quantity || 0) * (item.price || 0)).toFixed(2)}</td>
            </tr>
          `).join('')
          : '<tr><td colspan="4" class="no-items">No line items</td></tr>';

        return `
          <div class="door-section">
            <div class="door-header">Door #${door.door_number} ${door.location ? `- ${door.location}` : ''}</div>
            <table class="line-items-table">
              <thead>
                <tr><th class="desc-col">Description</th><th class="num-col">Qty</th><th class="num-col">Price</th><th class="num-col">Total</th></tr>
              </thead>
              <tbody>${lineItemsHtml}</tbody>
            </table>
            <div class="door-totals">
              <span>Parts: $${(door.parts_cost || 0).toFixed(2)}</span>
              <span>Labor: $${(door.labor_cost || 0).toFixed(2)}</span>
              <span>Hardware: $${(door.hardware_cost || 0).toFixed(2)}</span>
              <span class="door-total"><strong>Door Total: $${(door.total || 0).toFixed(2)}</strong></span>
            </div>
          </div>
        `;
      }).join('') || '<p>No doors</p>';

      // Generate print content
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Bid #${fullBid.id} - ${fullBid.customer_name}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 20px; color: #333; line-height: 1.2; font-size: 10px; }
            .header { border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 10px; }
            .company-name { font-size: 18px; font-weight: bold; color: #24324d; }
            .bid-title { font-size: 12px; color: #666; margin-top: 3px; }
            .section { margin-bottom: 10px; }
            .section-title { font-size: 11px; font-weight: bold; color: #24324d; border-bottom: 1px solid #ddd; padding-bottom: 3px; margin-bottom: 5px; }
            .info-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 3px; }
            .info-item { font-size: 9px; }
            .info-item strong { color: #24324d; }
            .door-section { margin-bottom: 8px; page-break-inside: avoid; }
            .door-header { font-size: 10px; font-weight: bold; color: #24324d; background: #f0f0f0; padding: 3px 5px; margin-bottom: 2px; }
            .line-items-table { width: 100%; border-collapse: collapse; font-size: 9px; }
            .line-items-table th, .line-items-table td { border: 1px solid #ddd; padding: 2px 4px; }
            .line-items-table th { background: #f8f8f8; font-weight: bold; font-size: 8px; }
            .desc-col { width: 60%; text-align: left; }
            .num-col { width: 13%; text-align: right; }
            .desc-cell { text-align: left; white-space: pre-wrap; word-break: break-word; }
            .num-cell { text-align: right; }
            .no-items { text-align: center; color: #999; font-style: italic; }
            .door-totals { display: flex; justify-content: flex-end; gap: 15px; font-size: 9px; padding: 3px 0; border-bottom: 1px solid #eee; }
            .door-total { font-weight: bold; }
            .totals { margin-top: 10px; text-align: right; }
            .totals-grid { display: inline-block; text-align: left; }
            .total-row { display: flex; justify-content: space-between; gap: 30px; padding: 2px 0; font-size: 10px; }
            .total-row.grand { font-size: 12px; font-weight: bold; border-top: 2px solid #333; padding-top: 5px; margin-top: 3px; }
            .footer { margin-top: 15px; text-align: center; font-size: 8px; color: #999; }
            @media print { body { padding: 10px; } @page { margin: 0.5in; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company-name">Scott Overhead Doors</div>
            <div class="bid-title">Bid #BID-${fullBid.id}${fullBid.reference_number ? ` | Offer #${fullBid.reference_number}` : ''}</div>
          </div>

          <div class="section">
            <div class="section-title">Customer Information</div>
            <div class="info-grid">
              <div class="info-item"><strong>Customer:</strong> ${fullBid.customer_name}</div>
              <div class="info-item"><strong>Bid Date:</strong> ${fullBid.bid_date ? new Date(fullBid.bid_date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}</div>
              <div class="info-item"><strong>Address:</strong> ${fullBid.customer_address || 'N/A'}</div>
              <div class="info-item"><strong>Phone:</strong> ${fullBid.customer_phone || 'N/A'}</div>
              <div class="info-item"><strong>Contact:</strong> ${fullBid.customer_contact || 'N/A'}</div>
              <div class="info-item"><strong>Status:</strong> ${fullBid.status?.toUpperCase() || 'DRAFT'}</div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">Door Details & Line Items</div>
            ${doorSections}
          </div>

          <div class="totals">
            <div class="totals-grid">
              <div class="total-row"><span>Total Parts:</span><span>$${fullBid.total_parts_cost?.toFixed(2) || '0.00'}</span></div>
              <div class="total-row"><span>Total Labor:</span><span>$${fullBid.total_labor_cost?.toFixed(2) || '0.00'}</span></div>
              <div class="total-row"><span>Total Hardware:</span><span>$${fullBid.total_hardware_cost?.toFixed(2) || '0.00'}</span></div>
              <div class="total-row"><span>Tax (8.75%):</span><span>$${fullBid.tax?.toFixed(2) || '0.00'}</span></div>
              <div class="total-row grand"><span>Grand Total:</span><span>$${fullBid.total_cost?.toFixed(2) || '0.00'}</span></div>
            </div>
          </div>

          <div class="footer">
            Generated on ${new Date().toLocaleDateString()} | Scott Overhead Doors
          </div>

          <script>
            window.onload = function() { window.print(); }
          </script>
        </body>
        </html>
      `;

      printWindow.document.write(printContent);
      printWindow.document.close();

    } catch (error) {
      console.error('Print error:', error);
      toast.error('Failed to generate print view');
    }
  };

  const generatePaginationItems = () => {
    const { totalPages } = paginationData;
    const items = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is small
      for (let i = 1; i <= totalPages; i++) {
        items.push(
          <Pagination.Item
            key={i}
            active={i === currentPage}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </Pagination.Item>
        );
      }
    } else {
      // Show truncated pagination for large number of pages
      const startPage = Math.max(1, currentPage - 2);
      const endPage = Math.min(totalPages, currentPage + 2);
      
      if (startPage > 1) {
        items.push(
          <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
            1
          </Pagination.Item>
        );
        if (startPage > 2) {
          items.push(<Pagination.Ellipsis key="start-ellipsis" />);
        }
      }
      
      for (let i = startPage; i <= endPage; i++) {
        items.push(
          <Pagination.Item
            key={i}
            active={i === currentPage}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </Pagination.Item>
        );
      }
      
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          items.push(<Pagination.Ellipsis key="end-ellipsis" />);
        }
        items.push(
          <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
            {totalPages}
          </Pagination.Item>
        );
      }
    }
    
    return items;
  };
  
  // If we have a bidId or estimateId, show the BidForm
  if (bidId || estimateId) {
    return (
      <Container fluid>
        <BidForm />
      </Container>
    );
  }
  
  // Otherwise, show the list of bids with search and pagination
  return (
    <Container fluid className="bids-page-container" style={{ 
      background: '#f5f7fa', 
      minHeight: '100vh', 
      padding: '32px 0 24px 0' 
    }}>
      <Card className="bids-list-card" style={{ 
        borderRadius: '14px', 
        boxShadow: '0 2px 16px rgba(36,50,77,0.08)', 
        border: 'none', 
        background: '#fff' 
      }}>
        <Card.Header style={{ 
          fontWeight: 700, 
          fontSize: '1.25rem', 
          background: '#f8fafc', 
          borderBottom: '1px solid #e9ecef', 
          color: '#24324d', 
          letterSpacing: '1px' 
        }}>
          <Row className="align-items-center">
            <Col>
              <span>Bids</span>
              {paginationData.totalItems > 0 && (
                <small className="text-muted ms-2">
                  ({paginationData.totalItems} total)
                </small>
              )}
            </Col>
          </Row>
        </Card.Header>
        
        <Card.Body>
          {/* Search Bar */}
          <Row className="mb-4">
            <Col md={6}>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="Search by customer name, site name, or bid ID..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  style={{ 
                    borderLeft: 'none',
                    fontSize: '0.95rem'
                  }}
                />
              </InputGroup>
            </Col>
          </Row>
          
          {loading ? (
            <div className="flex-center" style={{ minHeight: 120 }}>
              <p>Loading bids...</p>
            </div>
          ) : paginationData.totalItems === 0 ? (
            <div className="flex-center text-muted" style={{ minHeight: 120 }}>
              <p>
                {searchTerm ? 'No bids found matching your search.' : 'No bids found. Create a new bid from an estimate.'}
              </p>
            </div>
          ) : (
            <>
              {/* Results Summary */}
              <Row className="mb-3">
                <Col>
                  <small className="text-muted">
                    Showing {paginationData.startIndex + 1}-{paginationData.endIndex} of {paginationData.totalItems} bids
                    {searchTerm && ` matching "${searchTerm}"`}
                  </small>
                </Col>
              </Row>
              
              {/* Bids Table */}
              <Table striped bordered hover responsive className="bids-table" style={{ 
                background: '#fff', 
                borderRadius: '8px', 
                overflow: 'hidden' 
              }}>
                <thead style={{ background: '#f8fafc' }}>
                  <tr>
                    <th>Bid ID</th>
                    <th>Customer</th>
                    <th>Address</th>
                    <th>Total Cost</th>
                    <th>Bid Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginationData.currentBids.map(bid => (
                    <tr key={bid.id}>
                      <td data-label="Bid ID" style={{ fontWeight: 600, color: '#24324d' }}>
                        BID-{bid.id}
                        {bid.reference_number && (
                          <small className="text-muted ms-2">• Offer #{bid.reference_number}</small>
                        )}
                      </td>
                      <td data-label="Customer">{bid.customer_name}</td>
                      <td data-label="Address">{bid.site_address || 'No Address'}</td>
                      <td data-label="Total Cost">
                        <strong>${bid.total_cost?.toFixed(2) || '0.00'}</strong>
                      </td>
                      <td data-label="Bid Date" style={{ fontSize: '0.9rem' }}>
                        {formatDateOnly(bid.bid_date)}
                      </td>
                      <td data-label="Actions">
                        <div className="d-flex gap-2 flex-wrap">
                          <Link to={`/bids/${bid.id}`}>
                            <Button variant="outline-primary" size="sm" style={{
                              borderRadius: '6px',
                              fontWeight: 500
                            }}>
                              View/Edit
                            </Button>
                          </Link>
                          <Button
                            variant="success"
                            size="sm"
                            onClick={(e) => handlePrintBid(bid, e)}
                            style={{
                              borderRadius: '6px',
                              fontWeight: 500,
                              border: 'none',
                              backgroundColor: '#28a745',
                              color: 'white'
                            }}
                            title="Print Bid"
                          >
                            <FaPrint size={14} className="me-1" />
                            Print
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              
              {/* Pagination */}
              {paginationData.totalPages > 1 && (
                <Row className="mt-4">
                  <Col className="d-flex justify-content-center">
                    <Pagination className="mb-0">
                      <Pagination.Prev
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                      >
                        <FaChevronLeft size={14} />
                      </Pagination.Prev>
                      
                      {generatePaginationItems()}
                      
                      <Pagination.Next
                        disabled={currentPage === paginationData.totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                      >
                        <FaChevronRight size={14} />
                      </Pagination.Next>
                    </Pagination>
                  </Col>
                </Row>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Bids;